
import type { PosSaleLine, ManualDiscountDraft } from '../types';
import { DEFAULT_CURRENCY } from '@cfg/defines.ts';

interface NormalizeCurrencyOptions {
  allowNegative?: boolean;
}

export const normalizeCurrency = (
  value: number,
  options: NormalizeCurrencyOptions = {},
): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const rounded = Number(value.toFixed(2));
  if (!options.allowNegative && rounded < 0) {
    return 0;
  }
  if (Object.is(rounded, -0)) {
    return 0;
  }
  return rounded;
};

const clampCurrency = (value: number): number => normalizeCurrency(value);

export const normalizeRate = (rate?: number | null): number => {
  if (!rate) {
    return 0;
  }
  return rate > 1 ? rate / 100 : rate;
};

export interface LineAmounts {
  baseSubtotal: number;
  manualDiscountAmount: number;
  subtotalAfterDiscount: number;
  taxAmount: number;
  totalWithTax: number;
}

export const computeLineAmounts = (line: PosSaleLine): LineAmounts => {
  const unitPrice = line.useSubscription ? 0 : line.item.price?.value ?? 0;
  const quantity = line.quantity;
  const baseSubtotal = clampCurrency(unitPrice * quantity);

  let manualDiscountAmount = 0;
  const discount = line.manualDiscount;
  if (discount) {
    if (discount.mode === 'percentage') {
      manualDiscountAmount = clampCurrency(baseSubtotal * discount.value);
    } else {
      manualDiscountAmount = clampCurrency(discount.value);
    }
    if (manualDiscountAmount > baseSubtotal) {
      manualDiscountAmount = baseSubtotal;
    }
  }

  const subtotalAfterDiscount = clampCurrency(baseSubtotal - manualDiscountAmount);
  const taxRate = normalizeRate(line.item.price?.tax_rate ?? null);
  const taxAmount = clampCurrency(subtotalAfterDiscount * taxRate);
  const totalWithTax = clampCurrency(subtotalAfterDiscount + taxAmount);

  return {
    baseSubtotal,
    manualDiscountAmount,
    subtotalAfterDiscount,
    taxAmount,
    totalWithTax,
  };
};

export const computeOrderDiscount = (
  subtotalAfterLines: number,
  discount?: ManualDiscountDraft | null,
): { amount: number; subtotalAfter: number } => {
  if (!discount) {
    return { amount: 0, subtotalAfter: clampCurrency(subtotalAfterLines) };
  }

  let amount = 0;
  if (discount.mode === 'percentage') {
    amount = clampCurrency(subtotalAfterLines * discount.value);
  } else {
    amount = clampCurrency(discount.value);
  }
  if (amount > subtotalAfterLines) {
    amount = clampCurrency(subtotalAfterLines);
  }

  const subtotalAfter = clampCurrency(subtotalAfterLines - amount);
  return { amount, subtotalAfter };
};

export const hasManualDiscounts = (
  lines: PosSaleLine[],
  orderDiscount?: ManualDiscountDraft | null,
): boolean => {
  if (orderDiscount) {
    return true;
  }
  return lines.some((line) => Boolean(line.manualDiscount));
};

export { clampCurrency };

export interface OrderSummary {
  subtotal: number;
  taxes: number;
  total: number;
  discounts: number;
  shipping: number;
  currency: string;
  totalItems: number;
}

export const computeOrderSummary = (
  lines: PosSaleLine[],
  orderDiscount?: ManualDiscountDraft | null,
  options?: { shippingAmount?: number | null | undefined },
): OrderSummary => {
  let runningSubtotal = 0;
  let runningTaxes = 0;
  let detectedCurrency: string | undefined;
  let itemsCount = 0;
  let lineDiscounts = 0;

  for (const line of lines) {
    const currency = line.item.price?.currency;
    if (!detectedCurrency && currency) {
      detectedCurrency = currency;
    }
    const amounts = computeLineAmounts(line);
    runningSubtotal += amounts.subtotalAfterDiscount;
    runningTaxes += amounts.taxAmount;
    lineDiscounts += amounts.manualDiscountAmount;
    itemsCount += line.quantity;
  }

  const { amount: orderDiscountAmount, subtotalAfter } = computeOrderDiscount(runningSubtotal, orderDiscount);

  const shippingValue = options?.shippingAmount ?? 0;
  const safeCurrency = detectedCurrency ?? DEFAULT_CURRENCY;
  const subtotalRounded = Number(subtotalAfter.toFixed(2));
  const taxesRounded = Number(runningTaxes.toFixed(2));
  const shippingRounded = Number(Math.max(0, Number(shippingValue) || 0).toFixed(2));
  const totalRounded = Number((subtotalRounded + taxesRounded + shippingRounded).toFixed(2));
  const discountsRounded = Number((lineDiscounts + orderDiscountAmount).toFixed(2));

  return {
    subtotal: subtotalRounded,
    taxes: taxesRounded,
    total: totalRounded,
    discounts: discountsRounded,
    shipping: shippingRounded,
    currency: safeCurrency,
    totalItems: itemsCount,
  };
};
